"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiBatchLogger = exports.UnauthorizedResponseError = void 0;
const const_1 = require("../../const");
const SECONDS_IN_HOUR = 3600;
class UnauthorizedResponseError extends Error {
}
exports.UnauthorizedResponseError = UnauthorizedResponseError;
/**
 * ApiBatchLogger with accurate typing.
 * Allows to save log records in the local storage.
 * Saved log records are sent to API in batches.
 * After successful request sent logs are removed from the local storage.
 * In case of failed request exception is thrown and can be handled.
 * Mechanism for sending logs retry to send them in defined time interval.
 */
class ApiBatchLogger {
    host;
    localStorageKey;
    dryRun;
    onException;
    basicAuthUsername;
    basicAuthPassword;
    // In the future, it is worth considering whether it should be possible to modify
    // these parameters from the outside (from the logger initialization level).
    // Otherwise, replace these parameters with constant values
    syncInterval;
    maxRetries;
    bufferSize;
    ttl;
    startBatchSending;
    shouldTryToSend;
    timeoutId;
    constructor(options) {
        this.host = options.host;
        this.localStorageKey = options.localStorageKey;
        this.dryRun = options.dryRun;
        this.onException = options.onException;
        this.basicAuthUsername = options?.basicAuthUsername ?? null;
        this.basicAuthPassword = options?.basicAuthPassword ?? null;
        this.syncInterval = options.syncInterval ?? 30000;
        this.maxRetries = options.maxRetries ?? 5;
        this.bufferSize = options.bufferSize ?? 1000;
        this.ttl = options.ttl ?? 48 * SECONDS_IN_HOUR * 1000;
        this.startBatchSending = options.startBatchSending ?? true;
        this.shouldTryToSend = options.shouldTryToSend ?? true;
        this.startSending();
    }
    async sendRecordsIfAny() {
        const logs = this.getData();
        const outdatedLogKeys = Object.keys(logs).filter((key) => hasLogExpired(logs[key].eventTime, this.ttl));
        const recordKeys = Object.keys(logs).filter((key) => !outdatedLogKeys.includes(key));
        const records = recordKeys.map((key) => logs[key]);
        this.removeRecords(outdatedLogKeys);
        if (!recordKeys.length || !this.shouldTryToSend) {
            return;
        }
        let retries = 0;
        while (retries < this.maxRetries) {
            try {
                const response = await this.sendData(records);
                if (response.status === 401) {
                    throw new UnauthorizedResponseError(const_1.ERRORS.unauthorizedResponse);
                }
                if (!response.ok) {
                    throw new Error(`Api response: not ok - ${response.status} - ${response.statusText}`);
                }
                break;
            }
            catch (error) {
                if (error instanceof UnauthorizedResponseError && this.onException) {
                    this.onException(error);
                    break;
                }
                if (retries >= this.maxRetries - 1) {
                    if (!this.onException)
                        throw error;
                    this.onException(error);
                    break;
                }
                await sleep(Math.pow(++retries, 2));
            }
        }
        this.removeRecords(recordKeys);
    }
    async startSending() {
        if (this.startBatchSending) {
            await this.sendRecordsIfAny();
            this.timeoutId = window.setTimeout(() => this.startSending(), this.syncInterval);
        }
    }
    getApiHeaders() {
        const shouldIncludeCredentials = this.basicAuthUsername && this.basicAuthPassword;
        if (shouldIncludeCredentials) {
            const encodedCredentials = window.btoa(`${this.basicAuthUsername}:${this.basicAuthPassword}`);
            return {
                "Content-Type": "application/json",
                Authorization: `Basic ${encodedCredentials}`,
            };
        }
        return {
            "Content-Type": "application/json",
        };
    }
    getFetchConfig() {
        return {
            credentials: "include",
            redirect: "follow",
            cache: "no-cache",
            mode: "cors",
        };
    }
    getData() {
        const records = {};
        for (const key of Object.keys(window.localStorage)) {
            if (key.startsWith(this.localStorageKey)) {
                const item = window.localStorage.getItem(key);
                if (item) {
                    const record = JSON.parse(item);
                    if (record && isValidLogRecord(record)) {
                        records[key] = record;
                    }
                }
            }
        }
        return records;
    }
    removeRecords(recordKeys) {
        recordKeys.forEach((key) => window.localStorage.removeItem(key));
    }
    async sendData(data) {
        const url = this.host + `?dryRun=${this.dryRun}`;
        return await fetch(url, {
            headers: this.getApiHeaders(),
            method: "POST",
            body: JSON.stringify(data),
            ...this.getFetchConfig(),
        });
    }
    getRandomKey() {
        return `${this.localStorageKey}:${new Date().getTime()}:${Math.random()}`;
    }
    async report(record) {
        const storedKeys = Object.entries(this.getData())
            .sort((entryA, entryB) => sortLogRecordByTime(entryA[1], entryB[1]))
            .map(([key]) => key);
        this.removeRecords(storedKeys.slice(this.bufferSize - 1));
        window.localStorage.setItem(this.getRandomKey(), JSON.stringify(record));
        return record;
    }
}
exports.ApiBatchLogger = ApiBatchLogger;
function sleep(s) {
    return new Promise((resolve) => setTimeout(resolve, s * 1000));
}
function isValidLogRecord(record) {
    const allowedKeys = [
        "event",
        "app",
        "eventTime",
        "location",
        "userAgent",
        "viewport",
        "extra",
        "environment",
        "user",
    ];
    const recordKeys = Object.keys(record);
    return recordKeys.every((key) => allowedKeys.includes(key));
}
function hasLogExpired(time, ttl) {
    return new Date().getTime() - ttl > new Date(time).getTime();
}
function sortLogRecordByTime(recordA, recordB) {
    return new Date(recordB.eventTime).getTime() - new Date(recordA.eventTime).getTime();
}
//# sourceMappingURL=ApiBatchLogger.js.map